// Unified supplier "action items" — normalizes the four request sources (RFQ /
// direct orders / samples / product inquiries) into ONE status-classified feed.
// A supplier thinks "what needs my response", not "which object type" — so we
// bucket by STATUS and keep the object type as a `kind` label + a kind-specific
// action. Used by both the Home "needs your response" block and the Requests tab.
//
// Item shape: { key, kind, bucket, sourceId, requestId, buyerId, status,
//   createdAt, waitingSince, title, qty, unit, action }
//   kind    ∈ rfq | direct | sample | inquiry
//   bucket  ∈ needs_response | my_offers | accepted | finished
//   action  ∈ submit_offer | confirm_order | approve_sample | reply_inquiry
//             | view_offer | view_order | view_sample | view_inquiry
//
// SCALE NOTE: this merges 4-5 PostgREST queries client-side. At marketplace scale
// (the open-RFQ pool can be large) a security_invoker VIEW `supplier_action_items`
// (UNION ALL, server ORDER BY + range pagination) should replace
// fetchSupplierActionItems with the SAME output shape — the UI won't change.

export const ACTION_BUCKETS = ['needs_response', 'my_offers', 'accepted', 'finished'];

// Prefer the viewer's language, then fall back — so an English/Chinese supplier
// doesn't see Arabic titles for buyer requests that carry all three.
const reqTitle = (r, lang) => (r && (r[`title_${lang}`] || r.title_en || r.title_ar || r.title_zh)) || '';
const prodTitle = (p, lang) => (p && (p[`name_${lang}`] || p.name_en || p.name_ar || p.name_zh)) || '';
// A chat card ("[request:<uuid>]", "[img:…]", …) is markup for Chat.jsx, not
// words: shown as a preview it read "[request:93e7…]" on the supplier's home.
// Same prefixes as Chat.jsx isMediaMessage(). '' lets the UI fall back to the
// kind label ("رسالة").
const CARD_PREFIX = /^\[(img|vid|pdf|invoice|request):/;
export const messagePreview = (content) => {
  const text = String(content || '').trim();
  return CARD_PREFIX.test(text) ? '' : text.slice(0, 60);
};
const emptyCounts = () => ({ needs_response: 0, my_offers: 0, accepted: 0, finished: 0 });

const DIRECT_STATUSES = ['pending_supplier_confirmation', 'supplier_confirmed', 'paid', 'ready_to_ship', 'shipping', 'arrived', 'delivered'];

export async function fetchSupplierActionItems(sb, supplierId, lang = 'en') {
  if (!sb || !supplierId) return { items: [], counts: emptyCounts() };

  // Direct orders are `requests` pointing at one of the supplier's products.
  const { data: prods } = await sb.from('products').select('id').eq('supplier_id', supplierId);
  const myProductIds = (prods || []).map((p) => p.id);

  const [targetedReqs, routedReqs, myOffers, directReqs, samples, inquiries, msgRows] = await Promise.all([
    // Quote requests the BUYER aimed at this supplier when they created them.
    sb.from('requests')
      .select('id,title_ar,title_en,title_zh,buyer_id,status,quantity,unit,created_at')
      .eq('target_supplier_id', supplierId)
      .in('status', ['open', 'offers_received'])
      .then((r) => r.data || []),
    // Quote requests ROUTED to this supplier — by an admin, implicitly by the
    // supplier holding a public link token, or (since 2026-10-11) automatically
    // as an open direct request: source 'open_pool', every operational
    // supplier, written by triggers in 20261011224335. There is still no browse
    // screen: routing IS how a supplier learns a request exists, so this list
    // is what makes the open pool visible on web with no client change. (The
    // app's SupplierRequestsScreen queries `requests` instead and gets the
    // same rows through the routed branch of RLS.)
    // The embed reads through request_routing, and the requests RLS policy
    // independently allows exactly these rows, so a stale routing row for a
    // request the supplier may no longer read returns null and is skipped.
    sb.from('request_routing')
      .select('source,created_at,requests(id,title_ar,title_en,title_zh,buyer_id,status,quantity,unit,created_at)')
      .eq('supplier_id', supplierId)
      .then((r) => r.data || []),
    sb.from('offers')
      // requests.status rides along so a cancelled request can be SEEN FROM THE
      // LIST. Without it the row looked live and the supplier only found out by
      // clicking through into a dead request.
      .select('id,request_id,status,created_at,requests(title_ar,title_en,title_zh,buyer_id,status)')
      .eq('supplier_id', supplierId)
      .then((r) => r.data || []),
    myProductIds.length
      ? sb.from('requests')
          .select('id,title_ar,title_en,title_zh,buyer_id,status,quantity,unit,created_at,product_ref')
          .in('product_ref', myProductIds).in('status', DIRECT_STATUSES)
          .then((r) => r.data || [])
      : Promise.resolve([]),
    sb.from('samples')
      .select('id,status,quantity,created_at,buyer_id,products(name_ar,name_en,name_zh)')
      .eq('supplier_id', supplierId)
      .then((r) => r.data || []),
    sb.from('product_inquiries')
      .select('id,status,created_at,buyer_id,product_id,products(name_ar,name_en,name_zh)')
      .eq('supplier_id', supplierId)
      .then((r) => r.data || []),
    // Inbound messages awaiting this supplier's reply (buyer → supplier, unread).
    sb.from('messages')
      .select('id,sender_id,content,created_at')
      .eq('receiver_id', supplierId).eq('is_read', false)
      .order('created_at', { ascending: true })
      .then((r) => r.data || []),
  ]);

  // Requests I've already offered on leave the RFQ "needs_response" pool — they
  // are represented by my offer instead (my_offers / accepted / finished).
  const offeredReqIds = new Set((myOffers || []).map((o) => o.request_id));
  const items = [];

  // Targeted and routed are the same thing to a supplier: a request that
  // arrived. Merged before bucketing, and de-duplicated because a buyer can
  // target a supplier the admin also routed it to.
  const arrivedById = new Map();
  for (const r of targetedReqs) arrivedById.set(r.id, r);
  for (const row of routedReqs) {
    const r = row?.requests;
    if (!r) continue;                                   // filtered by RLS
    if (!['open', 'offers_received'].includes(String(r.status || '').toLowerCase())) continue;
    if (!arrivedById.has(r.id)) arrivedById.set(r.id, r);
  }

  // (a) Quote requests that arrived for this supplier — needs a response unless I offered.
  for (const r of arrivedById.values()) {
    if (offeredReqIds.has(r.id)) continue;
    items.push({
      key: `rfq:${r.id}`, kind: 'rfq', bucket: 'needs_response',
      sourceId: r.id, requestId: r.id, buyerId: r.buyer_id, status: r.status,
      createdAt: r.created_at, waitingSince: r.created_at,
      title: reqTitle(r, lang), qty: r.quantity, unit: r.unit, action: 'submit_offer',
    });
  }

  // (a') My submitted offers.
  for (const o of myOffers) {
    // The REQUEST being cancelled is a different fact from the OFFER's status,
    // and it outranks it here. Cancelling a request never touches the offers on
    // it, so a quote on a cancelled request stayed in 'my_offers' with a live
    // "view offer" link -- a row that looked actionable and led nowhere.
    //
    // It moves to 'finished' rather than being dropped: a supplier's quote
    // disappearing from the list with no explanation is the same harm as the
    // silence, one surface along. 'finished' already means over-and-visible,
    // which is exactly this.
    const requestCancelled = String(o.requests?.status || '').toLowerCase() === 'cancelled';
    const bucket = requestCancelled ? 'finished'
      : o.status === 'accepted' ? 'accepted'
        : o.status === 'rejected' ? 'finished'
          : 'my_offers';
    items.push({
      key: `offer:${o.id}`, kind: 'rfq', bucket,
      sourceId: o.id, requestId: o.request_id, buyerId: o.requests?.buyer_id, status: o.status,
      createdAt: o.created_at, waitingSince: o.created_at,
      // Carried so the ROW can say so, not just the screen behind it.
      requestCancelled,
      title: reqTitle(o.requests, lang),
      action: requestCancelled ? null : bucket === 'my_offers' ? 'view_offer' : 'view_order',
    });
  }

  // (b) Direct purchase orders.
  for (const r of directReqs) {
    const bucket = r.status === 'pending_supplier_confirmation' ? 'needs_response'
      : r.status === 'delivered' ? 'finished' : 'accepted';
    items.push({
      key: `direct:${r.id}`, kind: 'direct', bucket,
      sourceId: r.id, requestId: r.id, buyerId: r.buyer_id, status: r.status,
      createdAt: r.created_at, waitingSince: r.created_at,
      title: reqTitle(r, lang), qty: r.quantity, unit: r.unit,
      action: bucket === 'needs_response' ? 'confirm_order' : 'view_order',
    });
  }

  // (c) Sample requests (status: pending / approved / rejected).
  for (const s of samples) {
    const bucket = s.status === 'pending' ? 'needs_response' : s.status === 'approved' ? 'accepted' : 'finished';
    items.push({
      key: `sample:${s.id}`, kind: 'sample', bucket,
      sourceId: s.id, requestId: null, buyerId: s.buyer_id, status: s.status,
      createdAt: s.created_at, waitingSince: s.created_at,
      title: prodTitle(s.products, lang), qty: s.quantity,
      action: bucket === 'needs_response' ? 'approve_sample' : 'view_sample',
    });
  }

  // (d) Product inquiries (status: open / answered).
  for (const q of inquiries) {
    const bucket = q.status === 'open' ? 'needs_response' : 'finished';
    items.push({
      key: `inquiry:${q.id}`, kind: 'inquiry', bucket,
      sourceId: q.id, requestId: null, buyerId: q.buyer_id, status: q.status,
      createdAt: q.created_at, waitingSince: q.created_at,
      title: q.products?.[`name_${lang}`] || q.products?.name_en || q.products?.name_ar || '',
      action: bucket === 'needs_response' ? 'reply_inquiry' : 'view_inquiry',
    });
  }

  // (e) Inbound messages awaiting this supplier's reply — one item per conversation.
  const bySender = new Map();
  for (const m of msgRows) {
    if (!bySender.has(m.sender_id)) bySender.set(m.sender_id, { first: m, last: m });
    else bySender.get(m.sender_id).last = m;
  }
  for (const [sender, g] of bySender) {
    items.push({
      key: `msg:${sender}`, kind: 'message', bucket: 'needs_response',
      sourceId: sender, requestId: null, buyerId: sender, status: 'unread',
      createdAt: g.last.created_at, waitingSince: g.first.created_at,
      title: messagePreview(g.last.content), action: 'reply_message',
    });
  }

  const counts = emptyCounts();
  for (const it of items) counts[it.bucket] = (counts[it.bucket] || 0) + 1;
  return { items, counts };
}

// Oldest-waiting first (longest wait = most urgent) — the urgency ordering.
export function sortByUrgency(items) {
  return [...(items || [])].sort((a, b) => new Date(a.waitingSince || 0) - new Date(b.waitingSince || 0));
}

// "ينتظر ٣ ساعات" / "waiting 2 days" — urgency shown as wait duration, not a count.
export function waitingLabel(since, lang = 'ar') {
  const ms = Date.now() - new Date(since || Date.now()).getTime();
  const hrs = Math.max(0, Math.floor(ms / 3600000));
  const days = Math.floor(hrs / 24);
  const isAr = lang === 'ar', isZh = lang === 'zh';
  if (days >= 1) {
    if (isAr) return days === 1 ? 'ينتظر يوماً' : days === 2 ? 'ينتظر يومين' : `ينتظر ${days} أيام`;
    if (isZh) return `等待 ${days} 天`;
    return `waiting ${days} day${days > 1 ? 's' : ''}`;
  }
  const h = Math.max(1, hrs);
  if (isAr) return h === 1 ? 'ينتظر ساعة' : h === 2 ? 'ينتظر ساعتين' : `ينتظر ${h} ساعات`;
  if (isZh) return `等待 ${h} 小时`;
  return `waiting ${h} hour${h > 1 ? 's' : ''}`;
}
